import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  KeyboardAvoidingView,
  Platform
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { router } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

type PersonalityType = 'empathetic' | 'professional' | 'motivational' | 'gentle';

interface PersonalityOption {
  id: PersonalityType;
  title: string;
  description: string;
  traits: string[];
  example: string;
  prompt: string;
}

const personalityOptions: PersonalityOption[] = [
  {
    id: 'empathetic',
    title: 'Empático e acolhedor',
    description: 'Tom caloroso e compreensivo, focado no bem-estar emocional',
    traits: ['Caloroso', 'Compreensivo', 'Paciente', 'Encorajador'],
    example: '"Entendo que este é um momento difícil para você. Estou aqui para te apoiar em cada passo do seu caminho."',
    prompt: `Você é Bill, um mentor de recuperação extremamente empático e acolhedor. Sua personalidade é:

🎯 CARACTERÍSTICAS PRINCIPAIS:
- Caloroso e compreensivo, nunca julgamental
- Paciente e encorajador em todas as situações
- Foco no bem-estar emocional do usuário
- Linguagem afetuosa e acolhedora
- Validação constante dos sentimentos

💬 ESTILO DE COMUNICAÇÃO:
- Tom sempre caloroso e compreensivo
- Use expressões de apoio emocional
- Seja paciente com o ritmo do usuário
- Ofereça muito encorajamento
- Valide todos os sentimentos sem exceção
- Termine com mensagens de apoio e esperança

🚨 SITUAÇÕES DE CRISE:
- Aborde com máximo acolhimento
- Valide a dificuldade da situação
- Ofereça apoio emocional antes de soluções práticas
- Use linguagem muito suave e compreensiva

📅 MARCOS DE SOBRIEDADE:
- Celebre com muito carinho e emoção
- Reconheça a coragem e força emocional
- Compartilhe a alegria do momento

🎉 TOM GERAL:
Seja como um amigo querido que sempre entende e acolhe, oferecendo um abraço virtual em cada mensagem. Priorize o conforto emocional acima de tudo.`
  },
  {
    id: 'professional',
    title: 'Profissional e direto',
    description: 'Comunicação clara e objetiva, focada em resultados práticos',
    traits: ['Objetivo', 'Claro', 'Eficiente', 'Confiável'],
    example: '"Vamos revisar seu plano de tratamento. Aqui estão os próximos passos que recomendo para você."',
    prompt: `Você é Bill, um mentor de recuperação profissional e direto. Sua personalidade é:

🎯 CARACTERÍSTICAS PRINCIPAIS:
- Objetivo e claro em todas as comunicações
- Focado em resultados práticos e mensuráveis
- Eficiente no uso do tempo e palavras
- Confiável e consistente nas orientações
- Baseado em evidências e métodos comprovados

💬 ESTILO DE COMUNICAÇÃO:
- Comunicação clara e direta
- Estruture respostas de forma organizada
- Ofereça passos concretos e acionáveis
- Use dados e fatos quando relevante
- Seja conciso mas completo
- Termine com próximos passos claros

🚨 SITUAÇÕES DE CRISE:
- Avalie a situação objetivamente
- Ofereça plano de ação estruturado
- Foque em soluções práticas imediatas
- Mantenha tom profissional mas empático

📅 MARCOS DE SOBRIEDADE:
- Reconheça o progresso de forma factual
- Destaque os benefícios concretos alcançados
- Estabeleça metas claras para o futuro

🎉 TOM GERAL:
Seja um profissional competente que oferece orientação clara e confiável, sempre focado em resultados práticos e progresso mensurável.`
  },
  {
    id: 'motivational',
    title: 'Motivador e energético',
    description: 'Abordagem positiva e encorajadora para inspirar progresso',
    traits: ['Positivo', 'Enérgico', 'Inspirador', 'Otimista'],
    example: '"Você está fazendo um trabalho incrível! Cada pequeno passo é uma vitória importante no seu processo."',
    prompt: `Você é Bill, um mentor de recuperação motivador e energético. Sua personalidade é:

🎯 CARACTERÍSTICAS PRINCIPAIS:
- Positivo e otimista em todas as situações
- Enérgico e entusiasmado com o progresso
- Inspirador e motivacional constantemente
- Foco nas vitórias e possibilidades
- Linguagem energética e empolgante

💬 ESTILO DE COMUNICAÇÃO:
- Use tom energético e positivo
- Destaque sempre os aspectos positivos
- Celebrate cada pequena vitória
- Ofereça motivação constante
- Use linguagem inspiradora e otimista
- Termine com encorajamento energético

🚨 SITUAÇÕES DE CRISE:
- Mantenha foco no lado positivo
- Transforme desafios em oportunidades
- Ofereça perspectiva otimista
- Motive para ação positiva

📅 MARCOS DE SOBRIEDADE:
- Celebre com máximo entusiasmo
- Use muitos emojis de celebração
- Inspire para conquistas maiores
- Destaque o incrível progresso

🎉 TOM GERAL:
Seja um coach motivacional que sempre vê o lado positivo, inspira ação e celebra cada vitória como se fosse a maior conquista do mundo.`
  },
  {
    id: 'gentle',
    title: 'Gentil e reflexivo',
    description: 'Comunicação suave e thoughtful, respeitando o ritmo do paciente',
    traits: ['Suave', 'Reflexivo', 'Respeitoso', 'Cuidadoso'],
    example: '"Que tal conversarmos sobre como você está se sentindo hoje? Não há pressa, vamos no seu ritmo."',
    prompt: `Você é Bill, um mentor de recuperação gentil e reflexivo. Sua personalidade é:

🎯 CARACTERÍSTICAS PRINCIPAIS:
- Suave e delicado em todas as abordagens
- Reflexivo e thoughtful nas respostas
- Respeitoso com o ritmo individual
- Cuidadoso com palavras e timing
- Foco na introspecção e autoconhecimento

💬 ESTILO DE COMUNICAÇÃO:
- Use linguagem suave e delicada
- Faça perguntas reflexivas e profundas
- Respeite pausas e silêncios
- Ofereça espaço para introspecção
- Seja cuidadoso com temas sensíveis
- Termine com convites à reflexão

🚨 SITUAÇÕES DE CRISE:
- Aborde com extrema delicadeza
- Permita que a pessoa processe
- Ofereça presença mais que soluções
- Use tom muito suave e cuidadoso

📅 MARCOS DE SOBRIEDADE:
- Celebre de forma suave e significativa
- Convide à reflexão sobre o crescimento
- Reconheça a jornada interior

🎉 TOM GERAL:
Seja como um sábio gentil que caminha ao lado, oferecendo presença cuidadosa e espaço para crescimento pessoal no tempo certo.`
  }
];

const AdminPromptConfig = () => {
  const { user, userProfile } = useAuth();
  const [selectedPersonality, setSelectedPersonality] = useState<PersonalityType>('empathetic');
  const [customPrompt, setCustomPrompt] = useState('');
  const [creativity, setCreativity] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(150);
  const [sobrietyCongrats, setSobrietyCongrats] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfig, setOriginalConfig] = useState<{personality: PersonalityType, customPrompt: string, sobrietyCongrats: boolean}>({
    personality: 'empathetic',
    customPrompt: '',
    sobrietyCongrats: true
  });

  // Check if user is admin
  useEffect(() => {
    if (!user || !userProfile) {
      return; // Wait for both user and profile to load
    }

    if (userProfile.email !== 'admin@padrinho.app' && userProfile.role !== 'admin') {
      Alert.alert('Acesso Negado', 'Você não tem permissão para acessar esta área.', [
        { text: 'OK', onPress: () => router.back() }
      ]);
      return;
    }

    loadCurrentConfig();
  }, [user, userProfile]);

  const loadCurrentConfig = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('bill_config')
        .select('system_prompt, personality_type, sobriety_congrats')
        .eq('config_type', 'main')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading config:', error);
        throw error;
      }

      const personalityType = (data?.personality_type || 'empathetic') as PersonalityType;
      const customPromptText = data?.system_prompt || getPersonalityPrompt('empathetic');
      const sobrietyCongratsEnabled = data?.sobriety_congrats !== false;

      setSelectedPersonality(personalityType);
      setCustomPrompt(customPromptText);
      setSobrietyCongrats(sobrietyCongratsEnabled)
      setOriginalConfig({
        personality: personalityType,
        customPrompt: customPromptText,
        sobrietyCongrats: sobrietyCongratsEnabled
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading config:', error);
      Alert.alert('Erro', 'Não foi possível carregar a configuração atual.');
      // Load default config as fallback
      const defaultPersonality = 'empathetic';
      const defaultPrompt = getPersonalityPrompt(defaultPersonality);
      setSelectedPersonality(defaultPersonality);
      setCustomPrompt(defaultPrompt);
      setOriginalConfig({
        personality: defaultPersonality,
        customPrompt: defaultPrompt,
        sobrietyCongrats: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPersonalityPrompt = (personalityType: PersonalityType): string => {
    const personality = personalityOptions.find(p => p.id === personalityType);
    return personality?.prompt || personalityOptions[0].prompt;
  };

  const handlePersonalityChange = (personalityType: PersonalityType) => {
    setSelectedPersonality(personalityType);
    setCustomPrompt(getPersonalityPrompt(personalityType));
    checkForChanges(personalityType, getPersonalityPrompt(personalityType), sobrietyCongrats);
  };

  const handleCustomPromptChange = (text: string) => {
    setCustomPrompt(text);
    checkForChanges(selectedPersonality, text, sobrietyCongrats);
  };

  const checkForChanges = (personality: PersonalityType, prompt: string, sobrietyCongrats: boolean) => {
    const hasPersonalityChanged = personality !== originalConfig.personality;
    const hasPromptChanged = prompt !== originalConfig.customPrompt;
    const hasSobrietyCongratsChanged = sobrietyCongrats !== originalConfig.sobrietyCongrats;
    setHasChanges(hasPersonalityChanged || hasPromptChanged || hasSobrietyCongratsChanged);
  };

  const saveConfig = async () => {
    if (!hasChanges) return;

    try {
      setIsSaving(true);

      const configData = {
        system_prompt: customPrompt,
        personality_type: selectedPersonality,
        updated_at: new Date().toISOString(),
        updated_by: user?.id,
        sobriety_congrats: sobrietyCongrats
      };

      // First, try to update existing config
      const { error: updateError } = await supabase
        .from('bill_config')
        .update(configData)
        .eq('config_type', 'main');

      // If no rows were updated, create new config
      if (updateError && updateError.code === 'PGRST116') {
        const { error: insertError } = await supabase
          .from('bill_config')
          .insert({
            config_type: 'main',
            ...configData,
            created_at: new Date().toISOString(),
            created_by: user?.id
          });

        if (insertError) throw insertError;
      } else if (updateError) {
        throw updateError;
      }

      setOriginalConfig({
        personality: selectedPersonality,
        customPrompt: customPrompt,
        sobrietyCongrats: sobrietyCongrats
      });
      setHasChanges(false);

      Alert.alert(
        'Sucesso!', 
        'A personalidade do Bill foi atualizada com sucesso.',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error saving config:', error);
      Alert.alert('Erro', 'Não foi possível salvar as alterações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const resetToPersonalityDefault = () => {
    Alert.alert(
      'Restaurar Padrão da Personalidade',
      'Tem certeza que deseja restaurar o prompt padrão da personalidade selecionada?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Restaurar', 
          style: 'destructive',
          onPress: () => {
            const defaultPrompt = getPersonalityPrompt(selectedPersonality);
            setCustomPrompt(defaultPrompt);
            checkForChanges(selectedPersonality, defaultPrompt, sobrietyCongrats);
          }
        }
      ]
    );
  };

  const handleBack = () => {
    if (hasChanges) {
      Alert.alert(
        'Alterações não salvas',
        'Você tem alterações não salvas. Deseja sair mesmo assim?',
        [
          { text: 'Continuar editando', style: 'cancel' },
          { text: 'Sair sem salvar', style: 'destructive', onPress: () => router.back() }
        ]
      );
    } else {
      router.back();
    }
  };

  const handleSobrietyCongratsChange = () => {
    setSobrietyCongrats(!sobrietyCongrats);
    checkForChanges(selectedPersonality, customPrompt, !sobrietyCongrats);
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Carregando configuração...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.container} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Personalidade do Bill</Text>
          <TouchableOpacity 
            onPress={saveConfig} 
            style={[styles.saveButton, (!hasChanges || isSaving) && styles.saveButtonDisabled]}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#007AFF" />
            ) : (
              <Text style={[styles.saveButtonText, (!hasChanges || isSaving) && styles.saveButtonTextDisabled]}>
                Salvar
              </Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Instructions */}
          <View style={styles.instructionsCard}>
            <View style={styles.instructionsHeader}>
              <Ionicons name="information-circle" size={20} color="#007AFF" />
              <Text style={styles.instructionsTitle}>Personalidades Disponíveis</Text>
            </View>
            <Text style={styles.instructionsText}>
              Escolha uma das personalidades predefinidas para o Bill. Você também pode personalizar 
              o prompt específico após selecionar uma personalidade.
            </Text>
          </View>

          {/* Personality Selection */}
          <View style={styles.personalityCard}>
            <Text style={styles.sectionTitle}>Tipo de Personalidade</Text>

            {personalityOptions.map((personality) => (
              <TouchableOpacity
                key={personality.id}
                style={[
                  styles.personalityOption,
                  selectedPersonality === personality.id && styles.personalityOptionSelected
                ]}
                onPress={() => handlePersonalityChange(personality.id)}
              >
                <View style={styles.personalityHeader}>
                  <View style={styles.radioContainer}>
                    <View style={[
                      styles.radioButton,
                      selectedPersonality === personality.id && styles.radioButtonSelected
                    ]}>
                      {selectedPersonality === personality.id && (
                        <View style={styles.radioButtonInner} />
                      )}
                    </View>
                  </View>
                  <View style={styles.personalityInfo}>
                    <Text style={styles.personalityTitle}>{personality.title}</Text>
                    <Text style={styles.personalityDescription}>{personality.description}</Text>
                  </View>
                </View>

                <View style={styles.personalityTraits}>
                  {personality.traits.map((trait, index) => (
                    <View key={index} style={styles.traitBadge}>
                      <Text style={styles.traitText}>{trait}</Text>
                    </View>
                  ))}
                </View>

                <View style={styles.personalityExample}>
                  <Text style={styles.exampleLabel}>Exemplo de resposta:</Text>
                  <Text style={styles.exampleText}>{personality.example}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Custom Prompt Editor */}
          <View style={styles.editorCard}>
            <View style={styles.editorHeader}>
              <Text style={styles.editorTitle}>Prompt Personalizado</Text>
              <TouchableOpacity onPress={resetToPersonalityDefault} style={styles.resetButton}>
                <Ionicons name="refresh" size={16} color="#FF3B30" />
                <Text style={styles.resetButtonText}>Restaurar Padrão</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.editorDescription}>
              Personalize o prompt específico para a personalidade selecionada. O prompt será usado 
              como base para todas as interações do Bill.
            </Text>

            <View style={styles.textInputContainer}>
              <TextInput
                style={[styles.textInput, { color: '#151515' }]}
                value={customPrompt}
                onChangeText={handleCustomPromptChange}
                placeholder="Digite o prompt personalizado..."
                placeholderTextColor="#8E8E93"
                multiline
                textAlignVertical="top"
                scrollEnabled={false}
              />
              <View style={styles.characterCount}>
                <Text style={styles.characterCountText}>
                  {customPrompt.length} caracteres
                </Text>
              </View>
            </View>
          </View>

          {/* Sobriety Congratulations Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Parabéns de Sobriedade</Text>
            <Text style={styles.sectionDescription}>
              Configure como o Bill celebra marcos de sobriedade dos usuários
            </Text>

            <View style={styles.toggleContainer}>
              <View style={styles.toggleContent}>
                <Text style={styles.toggleLabel}>Parabéns automáticos por marcos</Text>
                <Text style={styles.toggleDescription}>
                  Bill enviará parabéns quando usuários atingirem marcos de sobriedade
                </Text>
              </View>
              <TouchableOpacity
                style={[styles.toggle, sobrietyCongrats && styles.toggleActive]}
                onPress={handleSobrietyCongratsChange}
              >
                <View style={[styles.toggleThumb, sobrietyCongrats && styles.toggleThumbActive]} />
              </TouchableOpacity>
            </View>

            {sobrietyCongrats && (
              <View style={styles.subSettings}>
                <Text style={styles.subSettingsTitle}>Configurações dos marcos:</Text>
                <Text style={styles.subSettingsNote}>
                  • Apenas marcos futuros são celebrados{'\n'}
                  • Marco de 24h é celebrado apenas uma vez{'\n'}
                  • Usuários com mais de 1 dia não recebem parabéns diários
                </Text>
              </View>
            )}
          </View>

          <View style={styles.divider} />

          {/* Changes Indicator */}
          {hasChanges && (
            <View style={styles.changesIndicator}>
              <Ionicons name="alert-circle" size={16} color="#FF9500" />
              <Text style={styles.changesText}>Você tem alterações não salvas</Text>
            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#8E8E93',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 20,
  },
  saveButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  saveButtonTextDisabled: {
    color: '#8E8E93',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  instructionsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  instructionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginLeft: 8,
  },
  instructionsText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#8E8E93',
  },
  personalityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 16,
  },
  personalityOption: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  personalityOptionSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#007AFF08',
  },
  personalityHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  radioContainer: {
    marginRight: 12,
    marginTop: 2,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#E5E5EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonSelected: {
    borderColor: '#007AFF',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#007AFF',
  },
  personalityInfo: {
    flex: 1,
  },
  personalityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  personalityDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 18,
  },
  personalityTraits: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  traitBadge: {
    backgroundColor: '#F2F2F7',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 6,
  },
  traitText: {
    fontSize: 12,
    color: '#8E8E93',
    fontWeight: '500',
  },
  personalityExample: {
    backgroundColor: '#F9F9F9',
    padding: 12,
    borderRadius: 8,
  },
  exampleLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#8E8E93',
    marginBottom: 4,
  },
  exampleText: {
    fontSize: 14,
    color: '#000000',
    fontStyle: 'italic',
    lineHeight: 18,
  },
  editorCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  editorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  editorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#FF3B301A',
  },
  resetButtonText: {
    fontSize: 14,
    color: '#FF3B30',
    marginLeft: 4,
    fontWeight: '500',
  },
  editorDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 18,
    marginBottom: 16,
  },
  textInputContainer: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#F9F9F9',
  },
  textInput: {
    padding: 16,
    fontSize: 16,
    lineHeight: 22,
    color: '#000000',
    minHeight: 300,
  },
  characterCount: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E5EA',
    backgroundColor: '#F2F2F7',
  },
  characterCountText: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'right',
  },
  changesIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF95001A',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  changesText: {
    fontSize: 14,
    color: '#FF9500',
    marginLeft: 6,
    fontWeight: '500',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
    marginBottom: 16,
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  toggleContent: {
    flex: 1,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 18,
  },
  toggle: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#E5E5EA',
    padding: 4,
  },
  toggleActive: {
    backgroundColor: '#4CD964',
  },
  toggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFFFFF',
  },
  toggleThumbActive: {
    transform: [{ translateX: 20 }],
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E5EA',
    marginVertical: 20,
  },
  saveSection: {
    paddingVertical: 20,
  },
  saveButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  subSettings: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#015480',
  },
  subSettingsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#151515',
    marginBottom: 8,
  },
  subSettingsNote: {
    fontSize: 12,
    color: '#757C85',
    lineHeight: 18,
    fontWeight: '500',
  },
});

export default AdminPromptConfig;